import React from 'react';
import { Sparkles, Image as ImageIcon, Sliders } from 'lucide-react';
import { PageSection } from '../../../../types';
import { ActionLinkInput } from '../common/ActionLinkInput';

interface HeroEditorProps {
  section: PageSection;
  onUpdateContent: (updates: any) => void;
}

export const HeroEditor: React.FC<HeroEditorProps> = ({ section, onUpdateContent }) => {
  const content = section.content || {};

  const stockHeroImages = [
    { label: 'Kurumsal & İş Merkezi', url: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1600&auto=format&fit=crop' },
    { label: 'Lüks Motoryat & Deniz', url: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?q=80&w=1600&auto=format&fit=crop' },
    { label: 'Modern Teknoloji & Ofis', url: 'https://images.unsplash.com/photo-1497366216548-37526070297c?q=80&w=1600&auto=format&fit=crop' },
    { label: 'Mavi Koy & Gulet', url: 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d17?q=80&w=1600&auto=format&fit=crop' },
  ];

  return (
    <div className="space-y-6">
      {/* Title & Copy */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Metin ve Başlıklar</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Üst Rozet (Badge)</label>
          <input
            type="text"
            value={content.badge ?? ''}
            onChange={(e) => onUpdateContent({ badge: e.target.value })}
            placeholder="Örn: YENİ NESİL ÇÖZÜMLER"
            className="w-full bg-slate-900 border border-slate-700 text-xs font-semibold text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Ana Manşet Başlığı</label>
          <input
            type="text"
            value={content.title ?? ''}
            onChange={(e) => onUpdateContent({ title: e.target.value })}
            placeholder="Güçlü bir manşet başlığı..."
            className="w-full bg-slate-900 border border-slate-700 text-xs font-bold text-slate-100 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Açıklama / Alt Metin</label>
          <textarea
            value={content.description ?? ''}
            onChange={(e) => onUpdateContent({ description: e.target.value })}
            rows={3}
            placeholder="Ziyaretçiyi karşılayacak etkileyici açıklama..."
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 p-2.5 rounded-xl outline-none focus:border-blue-500 resize-none"
          />
        </div>
      </div>

      {/* Buttons */}
      <div className="space-y-3">
        <h4 className="text-xs font-bold text-slate-200">Eylem Butonları</h4>
        <ActionLinkInput
          label="Birincil (Ana) Buton"
          buttonText={content.buttonText ?? 'Hemen İnceleyin'}
          onButtonTextChange={(txt) => onUpdateContent({ buttonText: txt })}
          linkType={content.buttonLinkType || 'page'}
          onLinkTypeChange={(type) => onUpdateContent({ buttonLinkType: type })}
          linkValue={content.buttonLink || '/iletisim'}
          onLinkValueChange={(val) => onUpdateContent({ buttonLink: val })}
          whatsappPhone={content.whatsappPhone}
          onWhatsappPhoneChange={(phone) => onUpdateContent({ whatsappPhone: phone })}
          whatsappMessage={content.whatsappMessage}
          onWhatsappMessageChange={(msg) => onUpdateContent({ whatsappMessage: msg })}
          phoneNumber={content.phoneNumber}
          onPhoneNumberChange={(num) => onUpdateContent({ phoneNumber: num })}
        />

        <ActionLinkInput
          label="İkincil Buton (Opsiyonel)"
          buttonText={content.secondaryButtonText ?? 'Hakkımızda'}
          onButtonTextChange={(txt) => onUpdateContent({ secondaryButtonText: txt })}
          linkType={content.secondaryButtonLinkType || 'page'}
          onLinkTypeChange={(type) => onUpdateContent({ secondaryButtonLinkType: type })}
          linkValue={content.secondaryButtonLink || '/hakkimizda'}
          onLinkValueChange={(val) => onUpdateContent({ secondaryButtonLink: val })}
        />
      </div>

      {/* Background Image & Overlay */}
      <div className="p-4 rounded-2xl bg-slate-800/40 border border-slate-700/60 space-y-3.5">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-3.5 h-3.5 text-blue-400" />
          <h4 className="text-xs font-bold text-slate-200">Arka Plan Görseli</h4>
        </div>

        <div className="space-y-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Görsel URL</label>
          <input
            type="url"
            value={content.bgImageUrl ?? ''}
            onChange={(e) => onUpdateContent({ bgImageUrl: e.target.value })}
            placeholder="https://images.unsplash.com/..."
            className="w-full bg-slate-900 border border-slate-700 text-xs text-slate-200 px-3 py-2 rounded-xl outline-none focus:border-blue-500"
          />
        </div>

        {/* Stock Presets */}
        <div className="space-y-1.5 pt-1">
          <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Hazır Görseller</label>
          <div className="grid grid-cols-2 gap-2">
            {stockHeroImages.map((img, idx) => (
              <button
                key={idx}
                type="button"
                onClick={() => onUpdateContent({ bgImageUrl: img.url })}
                className="group relative h-16 rounded-xl overflow-hidden border border-slate-700 hover:border-blue-500 transition text-left"
              >
                <img src={img.url} alt={img.label} className="w-full h-full object-cover group-hover:scale-105 transition" />
                <div className="absolute inset-0 bg-slate-950/60 p-1.5 flex items-end">
                  <span className="text-[9px] font-bold text-white line-clamp-1">{img.label}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Overlay Opacity Slider */}
        <div className="space-y-1.5 pt-2">
          <div className="flex items-center justify-between">
            <label className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Koyuluk / Karartma Oranı</label>
            <span className="text-[10px] font-bold text-blue-400">%{content.bgOverlayOpacity ?? 70}</span>
          </div>
          <input
            type="range"
            min="0"
            max="100"
            value={content.bgOverlayOpacity ?? 70}
            onChange={(e) => onUpdateContent({ bgOverlayOpacity: parseInt(e.target.value) })}
            className="w-full accent-blue-600 cursor-pointer"
          />
        </div>
      </div>
    </div>
  );
};
